import { PAGE_TIMERS, type LookupTimers } from "./lookup.ts";

/**
 * `/ping`'s run (ADR-068's amendment "`/ping`", item 2, and its amendment "`/ping` measures the network", item 2):
 * five `ping` frames, one at once and one a second after it, each timed from its send to its `pong` on the page's
 * clock, and beside each, at the same moment, a probe that times the network from the browser's Resource Timing.
 * The run ends at 5 seconds, when the fifth countdown step has passed, or, with a `pong` or a probe still owed
 * then, when the last comes or 2 seconds after the last `ping`, whichever is first; anything not back by then is
 * lost, and a probe owed is aborted. A `pong` is matched by its `t` against the tokens the run sent and still waits
 * on, and a run's tokens are odd where the keepalive's are even (`pingToken`, `keepaliveToken`), so a keepalive's
 * `pong` never becomes a sample. Nothing here draws: the run tells its progress to the store, which words it.
 */

/**
 * How many pings a run sends.
 */
export const PING_COUNT = 5;

/**
 * Milliseconds between a run's pings.
 */
export const PING_INTERVAL_MS = 1000;

/**
 * Milliseconds after the last ping by which a `pong` or a probe must be back, or it counts as lost.
 */
export const PING_GRACE_MS = 2000;

/**
 * Half the range of a `ping`'s `t` (0 to 2^32 - 1), so an odd or an even token fills it.
 */
const TOKEN_HALF = 0x80000000;

/**
 * Times under this many milliseconds show one decimal.
 */
const DECIMAL_BELOW_MS = 10;

/**
 * A run's token for a ping sent now: odd, and a whole number the `t` field takes.
 *
 * @param now The page's clock in milliseconds.
 * @returns The token.
 * @example
 * pingToken(1000.7);
 * // 2001
 */
export function pingToken(now: number): number {
  return (Math.floor(now) % TOKEN_HALF) * 2 + 1;
}

/**
 * The keepalive's token for a ping sent now: the clock in whole milliseconds with its lowest bit cleared, so even,
 * and a whole number the `t` field takes.
 *
 * @param now The page's clock in milliseconds.
 * @returns The token.
 * @example
 * keepaliveToken(1001.2);
 * // 1000
 */
export function keepaliveToken(now: number): number {
  const ms = Math.floor(now) % (2 * TOKEN_HALF);
  return ms - (ms % 2);
}

/**
 * Round trips' numbers: how many came back, and their mean, least and greatest in milliseconds, 0 each when none
 * came back.
 */
export interface PingSummary {
  readonly replies: number;
  readonly averageMs: number;
  readonly minMs: number;
  readonly maxMs: number;
}

/**
 * Round trips' numbers, unrounded; the sentence rounds them (`formatMs`).
 *
 * @param samples The round trips in milliseconds.
 * @returns The summary.
 * @example
 * pingSummary([28, 41, 32, 30, 29]);
 * // { replies: 5, averageMs: 32, minMs: 28, maxMs: 41 }
 */
export function pingSummary(samples: readonly number[]): PingSummary {
  if (samples.length === 0) {
    return { replies: 0, averageMs: 0, minMs: 0, maxMs: 0 };
  }
  const total = samples.reduce((sum, sample) => sum + sample, 0);
  return { replies: samples.length, averageMs: total / samples.length, minMs: Math.min(...samples), maxMs: Math.max(...samples) };
}

/**
 * A time as `/ping`'s result shows it: under 10 ms to one decimal, rounded to the tenth, and whole milliseconds
 * from there, so a time that rounds up to 10.0 shows as 10.
 *
 * @param ms The time in milliseconds.
 * @returns The digits, without the unit.
 * @example
 * formatMs(0.36);
 * // "0.4"
 * formatMs(32.5);
 * // "33"
 */
export function formatMs(ms: number): string {
  const tenths = Math.round(ms * 10) / 10;
  return tenths < DECIMAL_BELOW_MS ? tenths.toFixed(1) : String(Math.round(ms));
}

/**
 * The two stamps of a Resource Timing entry a network round trip is taken from.
 */
export interface ResourceStamps {
  readonly requestStart: number;
  readonly responseStart: number;
}

/**
 * The network's round trip for one request: from the request going (`requestStart`) to the answer's first byte
 * (`responseStart`), both stamped by the browser's network stack whatever the main thread is doing, and neither
 * counting the connection's setup, which comes before the request goes.
 *
 * @param entry The request's Resource Timing entry.
 * @returns The round trip in milliseconds, or null when the browser withheld the stamps (0, as it gives a
 *   cross-origin request without `Timing-Allow-Origin` naming the page) or they are out of order.
 * @example
 * networkRoundTrip({ requestStart: 1200.3, responseStart: 1200.7 });
 * // 0.4 (to the stamps' precision)
 */
export function networkRoundTrip(entry: ResourceStamps): number | null {
  const { requestStart, responseStart } = entry;
  if (!(requestStart > 0) || !(responseStart > 0) || responseStart < requestStart) {
    return null;
  }
  return responseStart - requestStart;
}

/**
 * Starts one network probe beside a ping. It reports once: the round trip in milliseconds, or null when it got no
 * usable timing (the request failed or was refused, or the browser gave no stamps). It returns the abort the run
 * calls when it stops waiting; a report after that is ignored.
 */
export type PingProbe = (report: (ms: number | null) => void) => () => void;

/**
 * What a run tells as it goes: a ping sent, with how many are out so far, or the run's end with the socket's round
 * trips (`samples`) and the network's (`network`), each in the order it came back.
 */
export type PingProgress = { readonly kind: "counting"; readonly sent: number } | { readonly kind: "done"; readonly samples: readonly number[]; readonly network: readonly number[] };

/**
 * What a run sends through, probes with, runs on and tells its progress to.
 */
export interface PingRunOptions {
  /** Sends a `ping` with its token, answering false when there is no connection to send it on. */
  readonly send: (t: number) => boolean;
  /** Times the network beside each ping; with none, the run tells no network round trips. */
  readonly probe?: PingProbe;
  /** The page's clock in milliseconds. */
  readonly now: () => number;
  /** The timers, the page's by default. */
  readonly timers?: LookupTimers;
  /** Told as each ping goes and once as the run ends with a result; never told of a run that ends quietly. */
  readonly show: (progress: PingProgress) => void;
}

/**
 * One `/ping` run.
 *
 * @example
 * ```ts
 * const run = new PingRun({ send: (t) => connection.sendChat({ kind: "ping", t }), probe: httpProbe(base), now: () => performance.now(), show: (progress) => draw(progress) });
 * run.start();
 * run.receive(pongT);
 * ```
 */
export class PingRun {
  private readonly timers: LookupTimers;
  private readonly owed = new Map<number, number>();
  private readonly probes = new Map<number, (() => void) | null>();
  private readonly samples: number[] = [];
  private readonly network: number[] = [];
  private sent = 0;
  private settled = false;
  private timer: unknown = null;
  private live = false;

  /**
   * @param options The sender, the probe, the clock, the timers and where the progress goes.
   */
  public constructor(private readonly options: PingRunOptions) {
    this.timers = options.timers ?? PAGE_TIMERS;
  }

  /**
   * Whether the run is going: started, and neither finished nor ended.
   *
   * @returns True while it goes.
   */
  public get running(): boolean {
    return this.live;
  }

  /**
   * Starts the run with its first ping.
   *
   * @returns False when the first ping could not be sent, so the run never started and told nothing.
   */
  public start(): boolean {
    this.live = true;
    this.fire();
    return this.live;
  }

  /**
   * Takes a `pong`: one the run still waits on becomes a sample, and any other is ignored.
   *
   * @param t The `pong`'s token.
   */
  public receive(t: number): void {
    const sentAt = this.owed.get(t);
    if (!this.live || sentAt === undefined) {
      return;
    }
    this.owed.delete(t);
    this.samples.push(this.options.now() - sentAt);
    this.settleIfDone();
  }

  /**
   * Ends the run quietly, as its connection ends: its timer stops, every `pong` owed is forgotten, every probe owed
   * is aborted, and nothing more is told.
   */
  public end(): void {
    this.live = false;
    this.owed.clear();
    const aborts = [...this.probes.values()];
    this.probes.clear();
    for (const abort of aborts) {
      abort?.();
    }
    if (this.timer !== null) {
      this.timers.clear(this.timer);
      this.timer = null;
    }
  }

  /**
   * Sends the next ping and its probe and tells the countdown; after the last, waits for the fifth step's end.
   */
  private fire(): void {
    this.timer = null;
    const now = this.options.now();
    let t = pingToken(now);
    while (this.owed.has(t)) {
      t = (t + 2) % (2 * TOKEN_HALF);
    }
    if (!this.options.send(t)) {
      this.end();
      return;
    }
    this.owed.set(t, now);
    this.sent += 1;
    this.startProbe(this.sent);
    this.options.show({ kind: "counting", sent: this.sent });
    if (this.live) {
      this.timer = this.timers.set(() => (this.sent < PING_COUNT ? this.fire() : this.settle()), PING_INTERVAL_MS);
    }
  }

  /**
   * Starts the probe that goes with a ping, when the run has one. It is owed before it starts, so a report that
   * comes before the call returns is taken and nothing is left to abort.
   *
   * @param index The ping's place, from 1.
   */
  private startProbe(index: number): void {
    const probe = this.options.probe;
    if (probe === undefined) {
      return;
    }
    this.probes.set(index, null);
    const abort = probe((ms) => this.probed(index, ms));
    if (this.probes.has(index)) {
      this.probes.set(index, abort);
    }
  }

  /**
   * Takes a probe's report: a round trip becomes a network sample, and a report the run no longer waits on is
   * ignored.
   *
   * @param index The ping's place the probe went with.
   * @param ms The round trip, or null for none.
   */
  private probed(index: number, ms: number | null): void {
    if (!this.live || !this.probes.has(index)) {
      return;
    }
    this.probes.delete(index);
    if (ms !== null) {
      this.network.push(ms);
    }
    this.settleIfDone();
  }

  /**
   * Finishes once the fifth step has passed and nothing is owed.
   */
  private settleIfDone(): void {
    if (this.settled && this.owed.size === 0 && this.probes.size === 0) {
      this.finish();
    }
  }

  /**
   * Marks the fifth step's end: the result now if nothing is owed, or once the last `pong` or probe comes or the
   * grace ends.
   */
  private settle(): void {
    this.timer = null;
    this.settled = true;
    if (this.owed.size === 0 && this.probes.size === 0) {
      this.finish();
      return;
    }
    this.timer = this.timers.set(() => this.finish(), PING_GRACE_MS - PING_INTERVAL_MS);
  }

  /**
   * Ends the run and tells its round trips; every `pong` and probe still owed is lost, and each probe aborted.
   */
  private finish(): void {
    const samples = [...this.samples];
    const network = [...this.network];
    this.end();
    this.options.show({ kind: "done", samples, network });
  }
}
